/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import _ from 'lodash';
import {
  loadCollectionById,
  loadCollectionItemsbyId,
  getCollectionPreview,
  createCollection,
  updateCollection,
  deleteCollection,
} from '@/collection/api';
import { loadTaxonomyByKey } from '@/taxonomy/api';

const getTagId = (tag) => {
  const tagIds = _.map(tag, (item) => { return item.id; });
  if (tagIds && tagIds.length > 0) {
    return tagIds.join();
  }
  return null;
};

const getItemIds = (collectionItems) => {
  const items = _.map(collectionItems, (item) => { return item.itemId; });
  return items;
};

const isEnglishLanguage = (string) => {
  return /^[a-zA-Z0-9-]+$/.test(string);
};

const validateKey = (key) => {
  return isEnglishLanguage(key);
};

const validateSchema = (fieldName, value) => {
  if (fieldName === 'key') {
    if (_.isEmpty(value)) {
      return { field: fieldName, message: 'ระบุคีย์' };
    }
    if (!validateKey(value)) {
      return { field: fieldName, message: 'คีย์ต้องเป็นภาษาอังกฤษเท่านั้น' };
    }
  } else if (fieldName === 'title' && _.isEmpty(value)) {
    return { field: fieldName, message: 'ระบุคีย์' };
  }
  return { field: fieldName, message: null };
};

const state = {
  collectionDynamicState: 'load', // default
  collection: {
    title: null,
    key: null,
    itemIds: [],
    type: null,
    note: null,
    isPinned: false,
    sortBy: 'createdAt', // createdAt is Default Sort
    sortOrder: 'asc', // asc is Default Sort in Dynamic Collection
  },
  loadCollectionState: {
    isError: false,
    isLoading: false,
    error: null,
  },
  saveCollectionState: {
    isSaving: false,
    isSaved: false,
    error: null,
  },
  saveCollectionStateError: {
    key: null,
    title: null,
    note: null,
    description: null,
  },
  collectionItems: [],
  lastPage: null,
  tagsResult: [],
  filter: {},
};

const defaultState = {
  collectionDynamicState: 'load', // default
  collection: {
    title: null,
    key: null,
    itemIds: [],
    type: null,
    note: null,
    isPinned: false,
    sortBy: 'createdAt', // createdAt is Default Sort
    sortOrder: 'asc', // asc is Default Sort in Dynamic Collection
  },
  loadCollectionState: {
    isError: false,
    isLoading: false,
    error: null,
  },
  saveCollectionState: {
    isSaving: false,
    isSaved: false,
    error: null,
  },
  saveCollectionStateError: {
    key: null,
    title: null,
    note: null,
    description: null,
    contentCategory: null,
  },
  collectionItems: [],
  lastPage: null,
  tagsResult: [],
  filter: {},
};

const getters = {
  collectionLastPage: (state) => state.lastPage,
  loadCollectionState: (state) => state.loadCollectionState,
  saveCollectionState: (state) => state.saveCollectionState,
  collectionDynamicState: (state) => state.collectionDynamicState,
  isCollectionDynamic: (state) => state.collection.isDynamic,
  collectionType: (state) => state.collection.type,
  isCollectionSaving: (state) => state.saveCollectionState.isSaving,
  isCollectionSaved: (state) => state.saveCollectionState.isSaved,
  isCollectionLoading: (state) => state.loadCollectionState.isLoading,
  isCollectionError: (state) => state.loadCollectionState.isError,
  collection: (state) => state.collection,
  collectionItems: (state) => state.collectionItems,
  getCurrentTags: (state) => _.get(state.collection, 'tags', null),
  getTagsResult: (state) => _.get(state, 'tagsResult', null),
};


const mutations = {
  setCollection(state, collection) {
    state.collection = collection;
  },
  resetState(state) {
    Object.assign(state, _.cloneDeep(defaultState));
  },
  setCollectionAttribute(state, params) {
    state.collection[params.key] = params.value;
  },
  setCollectionFilterMethod(state, filterObject) {
    state.filter = _.cloneDeep(filterObject);
  },
  setCollectionItems(state, newState) {
    state.collectionItems = newState.payload;
  },
  setCollectionDynamicState(state, newState) {
    state.collectionDynamicState = newState;
  },
  setLastPage(state, lastPage) {
    state.lastPage = lastPage;
  },
  setTags(state, tagsArray) {
    state.collection.tags = tagsArray;
  },
  setTagsResult(state, tagsArray) {
    state.tagsResult = tagsArray;
  },
  setStateMessage(state, message) {
    switch (message.type) {
      case 'loading':
        state.loadCollectionState.isSaving = false;
        state.loadCollectionState.isSaved = true;
        state.loadCollectionState.error = null;
        break;
      case 'success':
        state.loadCollectionState.isError = false;
        state.loadCollectionState.isLoading = false;
        state.loadCollectionState.error = false;
        break;
      case 'error':
        state.loadCollectionState.isError = true;
        state.loadCollectionState.isLoading = false;
        state.loadCollectionState.error = message.payload;
        break;
      default:
        state.loadCollectionState.isLoading = false;
        state.loadCollectionState.isError = false;
        state.loadCollectionState.error = null;
        break;
    }
  },
  setSavingMessage(state, message) {
    switch (message.type) {
      case 'saving':
        state.saveCollectionState.isSaving = true;
        state.saveCollectionState.isSaved = false;
        state.saveCollectionState.error = null;
        break;
      case 'success':
        state.saveCollectionState.isSaving = false;
        state.saveCollectionState.isSaved = true;
        state.saveCollectionState.error = false;
        break;
      case 'error':
        state.saveCollectionState.isSaving = false;
        state.saveCollectionState.isSaved = false;
        state.saveCollectionState.error = message.payload;
        break;
      default:
        state.saveCollectionState.isSaving = false;
        state.saveCollectionState.isSaved = false;
        state.saveCollectionState.error = null;
        break;
    }
  },
  setCollectionErrorMessage(state, params) {
    state.saveCollectionStateError[params.field] = params.message;
  },
};

const actions = {
  async loadCollectionById({ commit }, collectionId) {
    commit('resetState');
    commit('setStateMessage', { type: 'loading' });
    try {
      const response = await loadCollectionById(collectionId);
      commit('setCollection', response.data);
      commit('setStateMessage', { type: 'success' });
    } catch (error) {
      commit('setStateMessage', { type: 'error', payload: error.response });
    }
  },
  async loadCollectionItemsById({
    dispatch,
    commit,
    state,
  }, params) {
    const { collectionDynamicState } = state;
    const { isDynamic } = state.collection;
    try {
      let response = null;
      if (isDynamic) {
        if (collectionDynamicState === 'preview') {
          const filter = {
            page: params.page,
            tagIds: getTagId(state.collection.tags),
            sortOrder: state.collection.sortOrder,
          };
          await dispatch('setCollectionFilterMethod', filter);
          response = await getCollectionPreview(state.filter, state.collection.id);
        } else {
          const filter = {
            page: params.page,
            tagIds: getTagId(state.collection.tags),
            sortOrder: state.collection.sortOrder,
          };
          await dispatch('setCollectionFilterMethod', filter);
          response = await loadCollectionItemsbyId(state.filter, state.collection.id);
        }
      } else {
        const filter = {
          ...params.filter,
          page: params.page,
          limit: 'all',
        };
        await dispatch('setCollectionFilterMethod', filter);
        response = await loadCollectionItemsbyId(state.filter, state.collection.id);
      }
      commit('setCollectionItems', { payload: response.data.data });
      commit('setLastPage', response.data.lastPage);
    } catch (error) {
      commit('setStateMessage', { type: 'error', payload: error.response });
    }
  },
  async createCollection({ commit }, collection) {
    try {
      commit('setSavingMessage', { type: 'saving' });
      await createCollection(collection);
      commit('setSavingMessage', { type: 'success' });
    } catch (error) {
      commit('setSavingMessage', { type: 'error', payload: error.response });
    }
  },
  async updateCollection({ commit, state }) {
    const { id, isDynamic } = state.collection;
    const { collection, collectionItems } = state;
    try {
      let response = null;
      commit('setSavingMessage', { type: 'saving' });
      if (isDynamic) {
        commit('setCollectionAttribute', { key: 'itemIds', value: [] });
        response = await updateCollection(id, collection);
      } else {
        commit('setCollectionAttribute', { key: 'itemIds', value: getItemIds(collectionItems) });
        response = await updateCollection(id, collection);
      }
      commit('setCollection', response.data);
      commit('setSavingMessage', { type: 'success' });
    } catch (error) {
      commit('setSavingMessage', { type: 'error', payload: error.response });
    }
  },
  async deleteCollection({ commit }, collectionId) {
    try {
      commit('setSavingMessage', { type: 'saving' });
      await deleteCollection(collectionId);
      commit('setSavingMessage', { type: 'success' });
    } catch (error) {
      commit('setSavingMessage', { type: 'error', payload: error.response });
    }
  },
  async searchTags({ dispatch, commit }, filter) {
    try {
      const response = await loadTaxonomyByKey('tag', filter);
      await dispatch('setTagsResult', response.data.data);
      commit('setStateMessage', { type: 'success' });
    } catch (error) {
      commit('setStateMessage', { type: 'error', payload: error.response });
    }
  },
  setLastPage({ commit }, lastPage) {
    commit('setLastPage', lastPage);
  },
  setCollection({ commit }, collection) {
    commit('setCollection', collection);
  },
  setCollectionItems({ commit }, collectionItems) {
    commit('setCollectionItems', collectionItems);
  },
  setTagsResult({ commit }, tagsResult) {
    commit('setTagsResult', tagsResult);
  },
  setCollectionDynamicState({ commit }, state) {
    commit('setCollectionDynamicState', state);
  },
  setCollectionFilterMethod({ commit }, filterObject) {
    commit('setCollectionFilterMethod', filterObject);
  },
  setCollectionErrorMessage({ commit }, payload) {
    commit('setCollectionErrorMessage', payload);
  },
  async setCollectionAttribute({ commit }, params) {
    if (params.key) {
      commit('setCollectionErrorMessage', validateSchema(params.key, params.value));
      commit('setCollectionAttribute', params);
    }
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
